import { useMemo } from 'react'
import { Input, Button } from '@fullscreendigital/carrefour-dex-fe'
import { Modal } from 'components/core-ui/Modal'
import { useFormik } from 'formik'
import { getInitialValues, getValidationSchema } from './helpers'
import { useNotification } from 'hooks'
import { formatErrorMessage } from 'utils/formatting'
import s from 'components/core-ui/Modal/Modal.modules.scss'
import { Grid } from '@mui/material'
import { makeCorePostRequest, makeCorePutRequest } from 'utils/api'
import { useMutation } from 'react-query'
import {
  getMetiFamiliesByRayonOptions,
  getMetiSubFamiliesByFamilyOptions,
  getMetiUBBySubFamilyOptions
} from 'components/shop-reception/admin/api'
import { AsyncAutocomplete } from 'components/core-ui/AsyncAutocomplete'

interface DLCProductsFormProps {
  isOpen: boolean
  closeModal: Function
  setShouldRefreshData: Function
  isEdit?: boolean
  editItem?: DLCProducts
  metiRayons: AutocompleteOption[]
  shopCodes: AutocompleteOption[]
}

const DLCProductsForm: React.FC<DLCProductsFormProps> = props => {
  const { isOpen, closeModal, isEdit, editItem, setShouldRefreshData, metiRayons, shopCodes } = props
  const { showNotification } = useNotification()

  const mutateDlcProduct = async values => {
    editItem
      ? await makeCorePutRequest(`/shop-reception/products/short-dlc/${editItem.id}`, values)
      : await makeCorePostRequest('/shop-reception/products/short-dlc', values)
  }

  const { isLoading, mutate } = useMutation(mutateDlcProduct, {
    onSuccess: () => {
      closeModal()
      showNotification({
        message: `Produsul a fost ${editItem ? 'actualizat' : 'creat'} cu succes.`,
        type: 'success'
      })

      setShouldRefreshData(true)
      formik.resetForm()
      closeModal()
    },
    onError: res => {
      showNotification({
        message: `A aparut o eroare la ${
          editItem ? 'actualizarea' : 'crearea'
        } produsului. ${formatErrorMessage(res)}`,
        type: 'error'
      })
    }
  })

  const initialValues = useMemo(() => getInitialValues(editItem, metiRayons), [editItem, metiRayons])
  const validationSchema = useMemo(() => getValidationSchema(), [editItem])

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: async (values: any) => {
      let shopType;
      let shopCode;

      const selectedShopCode = values.shopCode?.value || values.shopCode;

      if (['ALL', 'HYPER', 'SUPER', 'COLUMBUS', 'OTHER'].includes(selectedShopCode)) {
        shopType = selectedShopCode;
        shopCode = null;
      } else {
        shopCode = selectedShopCode;
        shopType = null;
      }

      const payload = {
        ...values,
        famCode: values.famCode?.value || values.famCode || null,
        rayCode: values.rayCode?.value || values.rayCode || null,
        sfaCode: values.sfaCode?.value || values.sfaCode || null,
        shopCode: shopCode,
        shopType: shopType,
        ubCode: values.ubCode?.value || values.ubCode || null
      }

      mutate(payload)
    }
  })

  const { data: metiFamiliesOptions } = getMetiFamiliesByRayonOptions(
    showNotification,
    formik.values.rayCode?.value
  )

  const { data: metiSubfamiliesOption } = getMetiSubFamiliesByFamilyOptions(
    showNotification,
    formik.values.famCode?.value
  )

  const { data: ubCodeOptions } = getMetiUBBySubFamilyOptions(
    showNotification,
    formik.values.sfaCode?.value
  )

  return (
    <Modal
      isModalOpen={isOpen}
      closeModal={closeModal}
      modalTitle={`${isEdit ? 'Editeaza' : 'Adauga'} regula produse cu DLC scurt`}
      modalWidth="50%"
    >
      <Grid container spacing={2}>
        <Grid xs={12} item>
          <AsyncAutocomplete
            variant="outlined"
            label="Magazin"
            name="shopCode"
            value={formik.values.shopCode}
            options={shopCodes || []}
            onChange={option => {
              formik.setFieldValue('shopCode', option)
              formik.setFieldValue('rayCode', null)
              formik.setFieldValue('famCode', null)
              formik.setFieldValue('sfaCode', null)
              formik.setFieldValue('ubCode', null)
            }}
          />
        </Grid>

        <Grid xs={12} item>
          <AsyncAutocomplete
            variant="outlined"
            label="Raion"
            name="rayCode"
            value={formik.values.rayCode}
            options={metiRayons || []}
            required
            onChange={option => {
              formik.setFieldValue('rayCode', option)
              formik.setFieldValue('famCode', null)
              formik.setFieldValue('sfaCode', null)
              formik.setFieldValue('ubCode', null)
            }}
            error={!!formik.errors.rayCode}
            helperText={formik.errors.rayCode as string}
          />
        </Grid>

        <Grid xs={12} item>
          <AsyncAutocomplete
            variant="outlined"
            label="Familie"
            name="famCode"
            value={formik.values.famCode}
            options={metiFamiliesOptions || []}
            onChange={option => {
              formik.setFieldValue('famCode', option)
              formik.setFieldValue('sfaCode', null)
              formik.setFieldValue('ubCode', null)
            }}
          />
        </Grid>

        <Grid xs={12} item>
          <AsyncAutocomplete
            variant="outlined"
            label="Subfamilie"
            name="sfaCode"
            value={formik.values.sfaCode}
            options={metiSubfamiliesOption || []}
            onChange={option => {
              formik.setFieldValue('sfaCode', option)
              formik.setFieldValue('ubCode', null)
            }}
          />
        </Grid>

        <Grid xs={12} item>
          <AsyncAutocomplete
            variant="outlined"
            label="Cod UB"
            name="ubCode"
            value={formik.values.ubCode}
            options={ubCodeOptions || []}
            onChange={option => formik.setFieldValue('ubCode', option)}
          />
        </Grid>

        <Grid xs={12} item>
          <Input
            type="text"
            value={formik.values.prodCode}
            onChange={event =>
              formik.setFieldValue('prodCode', (event.target as HTMLInputElement).value)
            }
            label="Cod produs"
            variant="outlined"
            style={{ width: '100%' }}
          />
        </Grid>

        <Grid xs={12} item className={s.modalButtons}>
          <Button variant="outlined" label="Anuleaza" onClick={() => closeModal()} />
          <Button
            variant="contained"
            label={'Salveaza'}
            onClick={() => formik.handleSubmit()}
            disabled={isLoading}
          />
        </Grid>
      </Grid>
    </Modal>
  )
}

export default DLCProductsForm
